/**
 * Composable for WebView detection and element hiding
 */
export const useWebView = () => {
  const nuxtApp = useNuxtApp();
  
  // Get WebView context from plugin
  const webView = nuxtApp.$webView || {
    isWebView: false,
    userAgent: null,
    detectionMethod: null,
    isPlatformWebView: () => false,
  };
  
  /**
   * Removes specified elements from DOM
   */
  const removeWebViewElements = () => {
    if (!process.client || !webView.isWebView) return;
    
    const selectorsToRemove = [
      // OnePass headers
      '.onepass-header',
      '[class*="onepass-header"]',
      '.onepass-header-wrapper',
      '.header-onepass',
      
      // Header spacers
      '.header-spacer',
      '[class*="header-spacer"]',
      '.spacer-header',
      
      // Headers
      'header',
      '.header',
      '.site-header',
      '.main-header',
      '.global-header',
      '.SharedHeader',
      
      // Footers
      'footer',
      '.footer',
      '[class*="footer"]',
      '.site-footer',
      '.main-footer',
      '.global-footer',
      '.SharedFooter',
      
      // Mobile footers
      '.mobile-footer',
      '.footer-mobile',
      '[class*="mobile-footer"]',
      '.mobile-navigation-footer',
      
      // Navigation
      '.navigation-bar',
      '.nav-bar',
      '.navbar',
      '.mobile-nav',
      '.mobile-navigation',
      '.bottom-navigation',
      '.navigation-mobile-bottom',
      '.NavigationMobileBottom',
      
      // Top notifications
      '.top-notification',
      '.notification-top',
      '[class*="top-notification"]',
      '.banner-notification',
      '.SharedTopNotification',
      
      // Cookie/consent banners
      '.cookie-banner',
      '.cookie-consent',
      '.gdpr-banner',
      '.consent-banner',
      
      // Chat widgets
      '.chat-widget',
      '.support-chat',
      '[class*="chat-button"]',
      '[class*="chat-container"]',
    ];
    
    // Function to remove elements
    const removeElements = () => {
      selectorsToRemove.forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            if (el && el.parentNode) {
              el.parentNode.removeChild(el);
              console.log(`WebView: Removed element with selector ${selector}`);
            }
          });
        } catch (e) {
          console.error(`WebView: Error removing selector ${selector}:`, e);
        }
      });
      
      // Also hide with CSS as backup
      selectorsToRemove.forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            if (el instanceof HTMLElement) {
              el.style.display = 'none';
              el.style.visibility = 'hidden';
              el.style.position = 'fixed';
              el.style.bottom = '-100vh';
            }
          });
        } catch (e) {
          // Silently fail for CSS application
        }
      });
    };
    
    // Remove elements immediately
    removeElements();
    
    // Set up MutationObserver to catch dynamically added elements
    const observer = new MutationObserver((mutations) => {
      let shouldRemove = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              
              // Check if the added element matches any of our selectors
              for (const selector of selectorsToRemove) {
                if (element.matches && element.matches(selector)) {
                  shouldRemove = true;
                  break;
                }
                // Also check if any child elements match
                if (element.querySelectorAll && element.querySelectorAll(selector).length > 0) {
                  shouldRemove = true;
                  break;
                }
              }
            }
          });
        }
      });
      
      if (shouldRemove) {
        // Debounce removals to avoid excessive processing
        setTimeout(removeElements, 10);
      }
    });
    
    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    
    // Also run at intervals to catch any missed elements
    const intervals = [100, 500, 1000, 2000, 5000];
    intervals.forEach(interval => {
      setTimeout(removeElements, interval);
    });
    
    // Clean up observer on unmount
    onUnmounted(() => {
      observer.disconnect();
    });
  };
  
  /**
   * Adjusts body and content styles for WebView
   */
  const adjustWebViewStyles = () => {
    if (!process.client || !webView.isWebView) return;
    
    // Adjust body styles
    document.body.style.paddingTop = '0';
    document.body.style.marginTop = '0';
    document.body.style.paddingBottom = '0';
    document.body.style.marginBottom = '0';
    
    // Adjust main content
    const mainElements = document.querySelectorAll('main, .main-content, [role="main"], .page-container');
    mainElements.forEach(el => {
      if (el instanceof HTMLElement) {
        el.style.paddingTop = '0';
        el.style.marginTop = '0';
        el.style.minHeight = '100vh';
      }
    });
    
    // Remove any fixed positioning offsets
    const wrappers = document.querySelectorAll('.content-wrapper, .page-wrapper, #__nuxt, #app');
    wrappers.forEach(el => {
      if (el instanceof HTMLElement) {
        el.style.paddingTop = '0';
        el.style.marginTop = '0';
      }
    });
  };
  
  /**
   * Initialize WebView adjustments
   */
  const initializeWebView = () => {
    if (!webView.isWebView) return;
    
    onMounted(() => {
      // Add webview class
      document.body.classList.add('webview');
      
      // Adjust styles
      adjustWebViewStyles();
      
      // Remove elements
      removeWebViewElements();
      
      console.log('WebView: Initialization complete', {
        method: webView.detectionMethod,
        userAgent: webView.userAgent,
      });
    });
  };
  
  return {
    isWebView: webView.isWebView,
    userAgent: webView.userAgent,
    detectionMethod: webView.detectionMethod,
    isPlatformWebView: webView.isPlatformWebView,
    initializeWebView,
    removeWebViewElements,
    adjustWebViewStyles,
  };
};
